import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { useAuth } from '../contexts/AuthContext';
import {
  FileText, Save, Eye, EyeOff, RefreshCw, CheckCircle, AlertCircle,
  Loader2, Search, ExternalLink, Monitor, Smartphone, ChevronLeft,
  Lock, Layers, ArrowRight, Undo2,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Section → live public route (for "Open live page"). Only rendered when known.
const PAGE_ROUTES = {
  home_page: '/', driver_opportunities: '/programs', faq: '/faq',
  contact_info: '/contact', footer: '/', about: '/about', policies: '/policies',
  programs_overview: '/programs', lease_on: '/lease-on', dispatch_services: '/dispatch-services',
  company_driver: '/company-driver',
};

const GROUP_LABELS = {
  cta: 'Call to Action', faq: 'FAQ', hero: 'Hero', about: 'About', overview: 'Overview',
  compensation: 'Compensation', company: 'Company', recruiting: 'Recruiting',
  general: 'General', phone: 'Phone', location: 'Location',
};

// Derive a human sub-section grouping purely from the existing content key.
const groupOf = (key) => {
  const t = (key || '').split('_');
  if (t.length <= 1) return { id: 'general', label: 'General' };
  const g = t[1];
  const m = g.match(/^([a-z]+?)(\d+)$/); // faq_q1 / faq_a1 → pair under "Item N"
  if (m) return { id: `item-${m[2]}`, label: `Item ${m[2]}` };
  return { id: g, label: GROUP_LABELS[g] || (g.charAt(0).toUpperCase() + g.slice(1)) };
};

const SiteContentEditorPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [content, setContent] = useState({});
  const [sectionLabels, setSectionLabels] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [canEdit, setCanEdit] = useState(false);

  const [activeSection, setActiveSection] = useState(null);
  const [search, setSearch] = useState('');
  const [edits, setEdits] = useState({}); // key -> {value?, visible?}
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [previewMode, setPreviewMode] = useState('desktop'); // desktop | mobile
  const [mobilePane, setMobilePane] = useState('pages'); // pages | editor | preview (small screens)

  useEffect(() => {
    if (user && !['super_admin', 'ops_admin'].includes(user.role)) navigate('/admin/dashboard');
  }, [user, navigate]);

  const fetchContent = useCallback(async () => {
    try {
      setLoading(true); setError(null);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/site-content`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.status === 403) { setError('Admin access required'); return; }
      if (!res.ok) throw new Error('Failed to fetch content');
      const data = await res.json();
      setContent(data.content || {});
      setSectionLabels(data.section_labels || {});
      setCanEdit(data.can_edit === true);
      setActiveSection((prev) => prev && (data.content || {})[prev] ? prev : Object.keys(data.content || {})[0] || null);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchContent(); }, [fetchContent]);

  const sections = useMemo(() => Object.keys(content), [content]);

  const curVal = (item, field) => {
    const e = edits[item.key];
    if (e && e[field] !== undefined) return e[field];
    return item[field];
  };
  const itemChanged = (item) => {
    const e = edits[item.key];
    if (!e) return false;
    return (e.value !== undefined && e.value !== item.value) || (e.visible !== undefined && e.visible !== item.visible);
  };
  const editField = (key, field, value) => {
    setEdits((p) => ({ ...p, [key]: { ...p[key], [field]: value } }));
    setSavedFlash(false);
  };

  const activeItems = content[activeSection] || [];
  const dirtyKeys = useMemo(
    () => Object.values(content).flat().filter(itemChanged).map((i) => i.key),
    [content, edits],
  );
  const sectionDirtyCount = (sec) => (content[sec] || []).filter(itemChanged).length;

  // Filter sections + items by search
  const filteredSections = useMemo(() => {
    if (!search.trim()) return sections;
    const q = search.toLowerCase();
    return sections.filter((sec) =>
      (sectionLabels[sec] || sec).toLowerCase().includes(q) ||
      (content[sec] || []).some((i) => (i.label || '').toLowerCase().includes(q) || (i.value || '').toLowerCase().includes(q) || i.key.toLowerCase().includes(q)),
    );
  }, [search, sections, content, sectionLabels]);

  const visibleActiveItems = useMemo(() => {
    if (!search.trim()) return activeItems;
    const q = search.toLowerCase();
    return activeItems.filter((i) => (i.label || '').toLowerCase().includes(q) || (i.value || '').toLowerCase().includes(q) || i.key.toLowerCase().includes(q));
  }, [activeItems, search]);

  // Group active items into sub-sections (derived from keys)
  const groups = useMemo(() => {
    const map = new Map();
    visibleActiveItems.forEach((it) => {
      const g = groupOf(it.key);
      if (!map.has(g.id)) map.set(g.id, { id: g.id, label: g.label, items: [] });
      map.get(g.id).items.push(it);
    });
    const ORDER = ['hero', 'about', 'overview', 'intro', 'requirements', 'compensation', 'benefits', 'services', 'company', 'general', 'recruiting', 'phone', 'location'];
    const rank = (grp) => {
      if (grp.id.startsWith('item-')) return 500 + (parseInt(grp.id.split('-')[1], 10) || 0);
      if (grp.id === 'cta') return 999;
      const i = ORDER.indexOf(grp.id);
      return i === -1 ? 400 : i;
    };
    return Array.from(map.values()).sort((a, b) => rank(a) - rank(b));
  }, [visibleActiveItems]);

  const saveAll = async () => {
    if (!dirtyKeys.length) return;
    setSaving(true); setError(null);
    const token = localStorage.getItem('token');
    const all = Object.values(content).flat();
    try {
      for (const key of dirtyKeys) {
        const item = all.find((i) => i.key === key);
        const e = edits[key] || {};
        const res = await fetch(`${API_URL}/api/site-content/${key}`, {
          method: 'PUT',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            value: e.value !== undefined ? e.value : item.value,
            visible: e.visible !== undefined ? e.visible : item.visible,
          }),
        });
        if (!res.ok) throw new Error(`Failed to save "${item.label || key}"`);
        const data = await res.json();
        setContent((prev) => {
          const next = { ...prev };
          const sec = item.section;
          if (next[sec]) next[sec] = next[sec].map((i) => (i.key === key ? data.content : i));
          return next;
        });
      }
      setEdits({});
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 3000);
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  const discardAll = () => setEdits({});

  if (!['super_admin', 'ops_admin'].includes(user?.role)) {
    return (
      <DashboardLayout>
        <div className="p-8 text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900">Access Denied</h2>
          <p className="text-gray-600 mt-2">Admin access required.</p>
        </div>
      </DashboardLayout>
    );
  }

  const liveRoute = PAGE_ROUTES[activeSection];
  const pageVisible = activeItems.filter((i) => curVal(i, 'visible')).length;
  const pageHidden = activeItems.length - pageVisible;
  const pageUpdated = activeItems.map((i) => i.updated_at).filter(Boolean).sort().slice(-1)[0];

  return (
    <DashboardLayout>
      <div className="max-w-[1500px] mx-auto px-4 sm:px-6 py-6" data-testid="site-content-editor-page">
        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-3 mb-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-900 rounded-lg flex items-center justify-center">
              <FileText className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Site Content</h1>
              <p className="text-sm text-gray-500">Manage public website copy — changes apply immediately, no redeploy.</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {dirtyKeys.length > 0 && (
              <span className="text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-3 py-1.5" data-testid="scm-dirty-badge">
                {dirtyKeys.length} unsaved change{dirtyKeys.length > 1 ? 's' : ''}
              </span>
            )}
            <button onClick={fetchContent} disabled={loading}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              data-testid="refresh-content-btn">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </button>
          </div>
        </div>

        {/* Permission banner (single, page-level — not repeated per field) */}
        {!canEdit ? (
          <div className="mb-4 bg-slate-50 border border-slate-200 rounded-lg p-3 flex items-start gap-2.5" data-testid="scm-readonly-banner">
            <Lock className="w-4 h-4 text-slate-500 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-slate-700">
              <strong>Read-only.</strong> Editing site content requires <strong>Super Admin</strong> authority (governance / RBAC).
              You are signed in as <strong>Operations Admin</strong>, so you can review all copy but not change it.
            </p>
          </div>
        ) : (
          <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-2.5">
            <CheckCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-blue-800">Changes apply immediately to the live public site and are recorded in the audit trail.</p>
          </div>
        )}

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2.5" data-testid="scm-error">
            <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {loading ? (
          <div className="text-center py-24" data-testid="scm-loading">
            <Loader2 className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-3" />
            <p className="text-gray-500 text-sm">Loading content…</p>
          </div>
        ) : sections.length === 0 ? (
          <div className="text-center py-16 bg-gray-50 rounded-xl border border-gray-200" data-testid="scm-empty">
            <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <h3 className="text-lg font-medium text-gray-900 mb-1">No content found</h3>
            <p className="text-gray-500 text-sm">Content is seeded on first load. Try Refresh.</p>
          </div>
        ) : (
          <>
            {/* Mobile pane switcher */}
            <div className="lg:hidden mb-4 grid grid-cols-3 gap-1 bg-gray-100 rounded-lg p-1" data-testid="scm-mobile-tabs">
              {['pages', 'editor', 'preview'].map((p) => (
                <button key={p} onClick={() => setMobilePane(p)}
                  data-testid={`scm-mobile-tab-${p}`}
                  className={`text-xs font-semibold py-2 rounded-md capitalize transition-colors ${mobilePane === p ? 'bg-white text-blue-900 shadow-sm' : 'text-gray-500'}`}>
                  {p}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-[260px_minmax(0,1fr)_360px] gap-5">
              {/* LEFT — Page/Section navigator */}
              <aside className={`${mobilePane === 'pages' ? 'block' : 'hidden'} lg:block`} data-testid="scm-navigator">
                <div className="bg-white border border-gray-200 rounded-xl overflow-hidden lg:sticky lg:top-4">
                  <div className="p-3 border-b border-gray-100">
                    <div className="relative">
                      <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                      <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search content…"
                        data-testid="scm-search"
                        className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
                    </div>
                  </div>
                  <nav className="p-2 max-h-[70vh] overflow-y-auto">
                    <p className="px-2 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-gray-400 flex items-center gap-1.5">
                      <Layers className="w-3.5 h-3.5" /> Pages & Sections
                    </p>
                    {filteredSections.map((sec) => {
                      const items = content[sec] || [];
                      const hidden = items.filter((i) => !curVal(i, 'visible')).length;
                      const dirty = sectionDirtyCount(sec);
                      const active = sec === activeSection;
                      return (
                        <button key={sec} data-testid={`scm-page-${sec}`}
                          onClick={() => { setActiveSection(sec); setMobilePane('editor'); }}
                          className={`w-full text-left px-3 py-2.5 rounded-lg mb-0.5 transition-colors group ${active ? 'bg-blue-50 border border-blue-200' : 'hover:bg-gray-50 border border-transparent'}`}>
                          <div className="flex items-center justify-between gap-2">
                            <span className={`text-sm font-medium truncate ${active ? 'text-blue-900' : 'text-gray-800'}`}>
                              {sectionLabels[sec] || sec}
                            </span>
                            {dirty > 0 && <span className="w-2 h-2 rounded-full bg-amber-500 flex-shrink-0" title={`${dirty} unsaved`} />}
                          </div>
                          <div className="text-[11px] text-gray-400 mt-0.5">
                            {items.length} field{items.length !== 1 ? 's' : ''}{hidden > 0 ? ` · ${hidden} hidden` : ''}
                          </div>
                        </button>
                      );
                    })}
                    {filteredSections.length === 0 && (
                      <p className="px-3 py-4 text-sm text-gray-400">No pages match “{search}”.</p>
                    )}
                  </nav>
                </div>
              </aside>

              {/* CENTER — Structured editor */}
              <main className={`${mobilePane === 'editor' ? 'block' : 'hidden'} lg:block min-w-0`} data-testid="scm-editor">
                <div className="bg-white border border-gray-200 rounded-xl">
                  {/* Editor header */}
                  <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-2 min-w-0">
                      <button className="lg:hidden text-gray-400 hover:text-gray-700" onClick={() => setMobilePane('pages')} data-testid="scm-back-to-pages">
                        <ChevronLeft className="w-5 h-5" />
                      </button>
                      <div className="min-w-0">
                        <h2 className="text-lg font-semibold text-gray-900 truncate">{sectionLabels[activeSection] || activeSection}</h2>
                        <p className="text-xs text-gray-400">
                          {pageVisible} visible{pageHidden > 0 ? ` · ${pageHidden} hidden` : ''}
                          {pageUpdated ? ` · updated ${new Date(pageUpdated).toLocaleDateString()}` : ''}
                        </p>
                      </div>
                    </div>
                    {liveRoute && (
                      <a href={liveRoute} target="_blank" rel="noreferrer" data-testid="scm-open-live"
                        className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-700 hover:text-blue-900">
                        <ExternalLink className="w-3.5 h-3.5" /> Open live page
                      </a>
                    )}
                  </div>

                  {/* Groups → fields */}
                  <div className="p-5 space-y-6">
                    {groups.map((grp, gi) => (
                      <section key={gi} data-testid={`scm-group-${grp.label.toLowerCase().replace(/\s+/g, '-')}`}>
                        <h3 className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-3">{grp.label}</h3>
                        <div className="space-y-4">
                          {grp.items.map((item) => {
                            const isLong = (curVal(item, 'value') || '').length > 90;
                            const vis = curVal(item, 'visible');
                            return (
                              <div key={item.key} data-testid={`content-item-${item.key}`}
                                className={`rounded-lg border p-4 transition-colors ${itemChanged(item) ? 'border-amber-300 bg-amber-50/40' : 'border-gray-200'}`}>
                                <div className="flex items-center justify-between gap-3 mb-2">
                                  <div className="min-w-0">
                                    <label className="block text-sm font-semibold text-gray-900 truncate">{item.label || item.key}</label>
                                    <span className="text-[10px] text-gray-400 font-mono">{item.key}</span>
                                  </div>
                                  <button onClick={() => canEdit && editField(item.key, 'visible', !vis)}
                                    disabled={!canEdit}
                                    data-testid={`visibility-toggle-${item.key}`}
                                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-medium rounded-full transition-colors flex-shrink-0 ${vis ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'} ${canEdit ? 'hover:opacity-80 cursor-pointer' : 'cursor-default opacity-90'}`}>
                                    {vis ? <><Eye className="w-3 h-3" /> Visible</> : <><EyeOff className="w-3 h-3" /> Hidden</>}
                                  </button>
                                </div>
                                {isLong ? (
                                  <textarea value={curVal(item, 'value') || ''} onChange={(e) => editField(item.key, 'value', e.target.value)}
                                    rows={4} disabled={!canEdit} data-testid={`input-${item.key}`}
                                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm resize-y disabled:bg-gray-50 disabled:text-gray-500" />
                                ) : (
                                  <input type="text" value={curVal(item, 'value') || ''} onChange={(e) => editField(item.key, 'value', e.target.value)}
                                    disabled={!canEdit} data-testid={`input-${item.key}`}
                                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm disabled:bg-gray-50 disabled:text-gray-500" />
                                )}
                                {itemChanged(item) && (
                                  <p className="mt-1.5 text-[11px] text-amber-700 flex items-center gap-1"><ArrowRight className="w-3 h-3" /> Unsaved change</p>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </section>
                    ))}
                    {groups.length === 0 && <p className="text-sm text-gray-400 py-6 text-center">No fields match your search on this page.</p>}
                  </div>
                </div>
              </main>

              {/* RIGHT — Preview + status */}
              <aside className={`${mobilePane === 'preview' ? 'block' : 'hidden'} lg:block`} data-testid="scm-preview">
                <div className="lg:sticky lg:top-4 space-y-4">
                  <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                    <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                      <span className="text-sm font-semibold text-gray-900">Preview</span>
                      <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5">
                        <button onClick={() => setPreviewMode('desktop')} data-testid="scm-preview-desktop"
                          className={`p-1.5 rounded-md ${previewMode === 'desktop' ? 'bg-white shadow-sm text-blue-900' : 'text-gray-400'}`}><Monitor className="w-4 h-4" /></button>
                        <button onClick={() => setPreviewMode('mobile')} data-testid="scm-preview-mobile"
                          className={`p-1.5 rounded-md ${previewMode === 'mobile' ? 'bg-white shadow-sm text-blue-900' : 'text-gray-400'}`}><Smartphone className="w-4 h-4" /></button>
                      </div>
                    </div>
                    <div className="p-4 bg-gray-50">
                      <div className={`mx-auto bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden transition-all ${previewMode === 'mobile' ? 'max-w-[240px]' : 'w-full'}`}>
                        <div className="bg-blue-900 px-4 py-2 text-[10px] text-blue-200 font-mono truncate">{liveRoute || 'bluelockfreights.com'}</div>
                        <div className="p-4 space-y-2.5">
                          {activeItems.filter((i) => curVal(i, 'visible')).length === 0 ? (
                            <p className="text-xs text-gray-400 italic">All fields on this page are hidden.</p>
                          ) : activeItems.filter((i) => curVal(i, 'visible')).map((i) => {
                            const lbl = (i.label || '').toLowerCase();
                            const val = curVal(i, 'value') || '';
                            if (lbl.includes('title') && (lbl.includes('hero') || i.key.includes('hero_title'))) return <p key={i.key} className="text-base font-bold text-gray-900 leading-tight">{val}</p>;
                            if (lbl.includes('title')) return <p key={i.key} className="text-sm font-bold text-gray-900">{val}</p>;
                            if (lbl.includes('subtitle') || lbl.includes('tagline')) return <p key={i.key} className="text-xs font-medium text-blue-700">{val}</p>;
                            if (lbl.includes('question')) return <p key={i.key} className="text-xs font-semibold text-gray-800 pt-1">{val}</p>;
                            if (lbl.includes('email') || lbl.includes('phone') || lbl.includes('location')) return <p key={i.key} className="text-[11px] text-gray-600"><span className="text-gray-400">{i.label}: </span>{val}</p>;
                            return <p key={i.key} className="text-[11px] text-gray-600 leading-relaxed">{val}</p>;
                          })}
                        </div>
                      </div>
                      <p className="mt-3 text-[10px] text-gray-400 text-center">Approximate preview of visible copy (reflects your current edits). Use “Open live page” for exact rendering.</p>
                    </div>
                  </div>

                  {/* Status / metadata */}
                  <div className="bg-white border border-gray-200 rounded-xl p-4" data-testid="scm-status">
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-2">Status</p>
                    <dl className="space-y-1.5 text-sm">
                      <div className="flex justify-between"><dt className="text-gray-500">Visible fields</dt><dd className="font-medium text-gray-900">{pageVisible} / {activeItems.length}</dd></div>
                      <div className="flex justify-between"><dt className="text-gray-500">Unsaved (page)</dt><dd className={`font-medium ${sectionDirtyCount(activeSection) ? 'text-amber-700' : 'text-gray-900'}`}>{sectionDirtyCount(activeSection)}</dd></div>
                      <div className="flex justify-between"><dt className="text-gray-500">Last updated</dt><dd className="font-medium text-gray-900">{pageUpdated ? new Date(pageUpdated).toLocaleDateString() : '—'}</dd></div>
                    </dl>
                  </div>
                </div>
              </aside>
            </div>
          </>
        )}
      </div>

      {/* Sticky save bar (super_admin, when dirty) */}
      {canEdit && dirtyKeys.length > 0 && (
        <div className="fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur border-t border-gray-200 shadow-[0_-4px_16px_rgba(0,0,0,0.06)]" data-testid="scm-save-bar">
          <div className="max-w-[1500px] mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
            <span className="text-sm text-gray-600">
              {savedFlash ? <span className="inline-flex items-center gap-1.5 text-green-600 font-medium"><CheckCircle className="w-4 h-4" /> Saved</span>
                : <><strong className="text-gray-900">{dirtyKeys.length}</strong> unsaved change{dirtyKeys.length > 1 ? 's' : ''}</>}
            </span>
            <div className="flex items-center gap-2">
              <button onClick={discardAll} disabled={saving} data-testid="scm-discard"
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-50">
                <Undo2 className="w-4 h-4" /> Discard
              </button>
              <button onClick={saveAll} disabled={saving} data-testid="scm-save-all"
                className="inline-flex items-center gap-2 px-5 py-2 text-sm font-semibold text-white bg-blue-900 rounded-lg hover:bg-blue-800 disabled:opacity-50">
                {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : <><Save className="w-4 h-4" /> Save changes</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default SiteContentEditorPage;
